import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, Clock, FileDown, Loader2, MessageCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, formatDate, formatRp, imgUrl, waLink } from "@/lib/api";
import { formatWa, useStoreInfo } from "@/lib/store";
import { downloadInvoice } from "@/lib/invoice";
import { StatusBadge } from "@/components/shop/StatusBadge";
import { Button } from "@/components/ui/button";

const Countdown = ({ until }) => {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const tick = () => setLeft(Math.max(0, new Date(until).getTime() - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [until]);
  const m = Math.floor(left / 60000), s = Math.floor((left % 60000) / 1000);
  return <span className="tnum" data-testid="payment-countdown">{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>;
};

export const waOrderText = (o) => `Halo EllMom Handmade, saya sudah memesan.\nNo. Pesanan: ${o.order_id}\n` +
  o.items.map((i) => `- ${i.name} x${i.qty}`).join("\n") + `\nTotal: ${formatRp(o.total)}\nStatus: ${o.status}`;

export const OrderSummary = ({ o }) => (
  <div className="rounded-lg border border-stone-200 bg-white p-5">
    <div className="divide-y divide-stone-100">
      {o.items.map((i, idx) => (
        <div key={idx} className="flex gap-3 py-2">
          <img src={imgUrl(i.image)} alt="" className="h-12 w-12 rounded object-cover" />
          <div className="flex-1 text-sm"><p className="font-medium">{i.name}</p><p className="text-xs text-stone-500">{Object.values(i.variant || {}).join(" · ")} × {i.qty}</p></div>
          <p className="tnum text-sm">{formatRp(i.subtotal)}</p>
        </div>
      ))}
    </div>
    <dl className="mt-3 space-y-1 border-t border-stone-200 pt-3 text-sm">
      <div className="flex justify-between"><dt>Subtotal</dt><dd className="tnum">{formatRp(o.subtotal)}</dd></div>
      <div className="flex justify-between"><dt>Ongkir ({o.courier.name} {o.courier.service})</dt><dd className="tnum">{formatRp(o.shipping_cost)}</dd></div>
      {o.tax?.charged_to_buyer && o.tax.amount > 0 && <div className="flex justify-between"><dt>{o.tax.name} ({o.tax.rate_percent}%)</dt><dd className="tnum" data-testid="order-tax">{formatRp(o.tax.amount)}</dd></div>}
      <div className="flex justify-between font-semibold"><dt>Total</dt><dd className="tnum">{formatRp(o.total)}</dd></div>
    </dl>
    <p className="mt-3 text-xs text-stone-500" data-testid="order-recipient">
      Kirim ke: {o.recipient.name}{o.recipient.phone ? ` (${o.recipient.phone})` : ""} — {[o.recipient.address, o.recipient.village, o.recipient.district && `Kec. ${o.recipient.district}`, o.recipient.city, o.recipient.province].filter(Boolean).join(", ")} {o.recipient.postal_code || ""}
    </p>
    {o.tracking_number && <p className="mt-2 text-sm">No. Resi: <span className="font-medium" data-testid="order-tracking-number">{o.tracking_number}</span></p>}
  </div>
);

const QrPanel = ({ o, onSimulate, simulating }) => (
  <div className="rounded-xl border border-stone-200 bg-white p-6 text-center" data-testid="qris-panel">
    <p className="text-sm font-medium">Scan QRIS dengan e-wallet atau m-banking</p>
    <div className="mx-auto mt-4 w-fit rounded-lg border border-stone-200 p-3">
      {o.payment?.qr_string ? <QRCodeSVG value={o.payment.qr_string} size={220} data-testid="qris-code" /> : <img src={o.payment?.qr_url} alt="QRIS" className="h-56 w-56" />}
    </div>
    <p className="tnum mt-4 text-2xl font-semibold text-amber-800" data-testid="payment-total">{formatRp(o.total)}</p>
    <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-stone-500"><Clock className="h-4 w-4" /> Bayar dalam <Countdown until={o.expires_at} /></p>
    <p className="mt-3 flex items-center justify-center gap-2 text-xs text-stone-500"><Loader2 className="h-3 w-3 animate-spin" /> Menunggu pembayaran, status diperbarui otomatis</p>
    {o.payment?.mode === "mock" && (
      <div className="mt-5 rounded-lg bg-amber-50 p-3 text-left text-xs text-amber-900">
        <p className="font-medium">Mode demo — payment gateway belum terhubung</p>
        <p className="mt-1">QR ini bukan QRIS asli. Gunakan tombol di bawah untuk mensimulasikan pembayaran.</p>
        <Button size="sm" variant="outline" className="mt-2" data-testid="simulate-payment-button" disabled={simulating} onClick={onSimulate}>Simulasikan Pembayaran</Button>
      </div>
    )}
  </div>
);

export default function Payment() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const [notFound, setNF] = useState(false);
  const [simulating, setSim] = useState(false);
  const [params] = useSearchParams();
  const [autoDone, setAutoDone] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.post(`/orders/${orderId}/refresh`);
      setO(r.data);
    } catch (e) {
      if (e?.response?.status === 404) setNF(true);
    }
  }, [orderId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (o?.status !== "pending") return undefined;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [o?.status, refresh]);

  const info = useStoreInfo();
  const store = { address: info.address, phone: formatWa(info.admin_whatsapp) };
  const isPaid = ['paid', 'packed', 'shipped', 'completed'].includes(o?.status);
  useEffect(() => {
    if (isPaid && params.get("invoice") === "1" && !autoDone) {
      setAutoDone(true);
      downloadInvoice(o, store);
    }
  }, [isPaid, params, autoDone, o]); // eslint-disable-line react-hooks/exhaustive-deps

  const simulate = async () => {
    setSim(true);
    try { setO((await api.post(`/orders/${orderId}/simulate-payment`)).data); toast.success("Pembayaran berhasil"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSim(false); }
  };

  if (notFound) return <p className="py-24 text-center text-stone-500">Pesanan tidak ditemukan.</p>;
  if (!o) return <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-700" /></div>;
  const paid = ["paid", "packed", "shipped", "completed"].includes(o.status);

  return (
    <div className="mx-auto grid max-w-5xl gap-8 px-4 py-8 md:grid-cols-2 md:px-6">
      <div>
        <p className="text-sm text-stone-500">No. Pesanan</p>
        <div className="flex items-center gap-3"><h1 className="text-2xl font-semibold" data-testid="payment-order-id">{o.order_id}</h1><StatusBadge status={o.status} testId="payment-status-badge" /></div>
        <p className="mt-1 text-xs text-stone-500">Dibuat {formatDate(o.created_at)}</p>
        <div className="mt-6">
          {o.status === "pending" && <QrPanel o={o} onSimulate={simulate} simulating={simulating} />}
          {paid && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-6 text-center" data-testid="payment-success">
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-700" />
              <p className="mt-3 text-lg font-semibold text-green-900">Pembayaran terverifikasi</p>
              <p className="mt-1 text-sm text-green-800">Dibayar {formatDate(o.paid_at)}. Pesanan kamu segera kami proses.</p>
              <Button className="mt-4" data-testid="download-invoice-button"
                onClick={() => downloadInvoice(o, store)}>
                <FileDown className="mr-2 h-4 w-4" /> Unduh Invoice PDF
              </Button>
            </div>
          )}
          {["expired", "cancelled"].includes(o.status) && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-800" data-testid="payment-expired">
              Waktu pembayaran habis atau dibatalkan. Silakan buat pesanan baru atau hubungi CS.
            </div>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" data-testid="check-payment-button" onClick={refresh}><RefreshCw className="mr-2 h-4 w-4" /> Cek Status</Button>
          <a href={waLink(waOrderText(o), info.admin_whatsapp)} target="_blank" rel="noreferrer" data-testid="payment-wa-button"
            className="inline-flex h-9 items-center rounded-md bg-green-600 px-4 text-sm font-medium text-white hover:bg-green-700"><MessageCircle className="mr-2 h-4 w-4" /> Kirim ke WhatsApp Admin</a>
        </div>
        <p className="mt-4 text-xs text-stone-500">Simpan nomor pesanan untuk <Link to="/lacak" className="text-amber-700 underline">melacak pesanan</Link>.</p>
      </div>
      <OrderSummary o={o} />
    </div>
  );
}
